import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import {
  KH_SUPPLY_METHODS,
  saveKhSupplyConfig,
  type KhSupplyConfig,
  type KhSupplyMethod,
} from '../../services/khSupplyService';

export interface KhSupplyFormProps {
  aquariumId: string;
  config: KhSupplyConfig | null;
  onSuccess: (config: KhSupplyConfig) => void;
  onCancel: () => void;
}

type Field = 'effluentFlow' | 'reactorHours' | 'effluentKh' | 'effluentCa' | 'solutionMl';

/** Allowed ranges (same as the API validator, src/validators/khSupply.ts). */
const RANGES: Record<Field, { min: number; max: number }> = {
  effluentFlow: { min: 0.1, max: 1000 },
  effluentKh: { min: 1, max: 60 },
  effluentCa: { min: 100, max: 2000 },
  reactorHours: { min: 0.5, max: 24 },
  solutionMl: { min: 0.1, max: 10000 },
};

function text(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/** '' → null, '2,5' → 2.5, invalid → NaN. */
function parse(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  return trimmed === '' ? null : Number(trimmed);
}

/** KH/Ca upkeep: method plus what is added in which strength. */
export function KhSupplyForm({ aquariumId, config, onSuccess, onCancel }: KhSupplyFormProps) {
  const { t } = useTranslation();
  const [method, setMethod] = useState<KhSupplyMethod>(config?.method ?? 'calcium_reactor');
  const [values, setValues] = useState<Record<Field, string>>({
    effluentFlow: text(config?.effluent_ml_per_min),
    effluentKh: text(config?.effluent_kh_dkh),
    effluentCa: text(config?.effluent_ca_mg_l),
    reactorHours: text(config?.reactor_hours_per_day ?? 24),
    solutionMl: text(config?.kh_solution_ml_per_day),
  });
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [saveFailed, setSaveFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reactor = method === 'calcium_reactor';
  const fields: Field[] = reactor ? ['effluentFlow', 'reactorHours', 'effluentKh', 'effluentCa'] : ['solutionMl'];
  const required: ReadonlySet<Field> = new Set(reactor ? ['effluentFlow', 'effluentKh'] : ['solutionMl']);

  function setValue(field: Field) {
    return (e: ChangeEvent<HTMLInputElement>) => setValues((v) => ({ ...v, [field]: e.target.value }));
  }

  function validate(): Partial<Record<Field, number | null>> | null {
    const next: Partial<Record<Field, string>> = {};
    const parsed: Partial<Record<Field, number | null>> = {};
    for (const field of fields) {
      const n = parse(values[field]);
      const { min, max } = RANGES[field];
      if (n === null) {
        if (required.has(field)) next[field] = t('dosing.khSupply.form.errors.required');
      } else if (!Number.isFinite(n) || n < min || n > max) {
        next[field] = t('dosing.khSupply.form.errors.range', { min, max });
      }
      parsed[field] = n;
    }
    setErrors(next);
    return Object.keys(next).length === 0 ? parsed : null;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaveFailed(false);
    const parsed = validate();
    if (!parsed) return;
    setIsSubmitting(true);
    try {
      onSuccess(
        await saveKhSupplyConfig(aquariumId, reactor
          ? {
            method,
            effluent_ml_per_min: parsed.effluentFlow ?? null,
            effluent_kh_dkh: parsed.effluentKh ?? null,
            effluent_ca_mg_l: parsed.effluentCa ?? null,
            // Empty = runs around the clock.
            reactor_hours_per_day: parsed.reactorHours ?? null,
          }
          : { method, kh_solution_ml_per_day: parsed.solutionMl ?? null }),
      );
    } catch {
      setSaveFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="kh-supply-form">
      <Stack gap="4">
        {saveFailed && (
          <Alert tone="danger" data-testid="kh-supply-save-error">
            {t('dosing.khSupply.form.saveError')}
          </Alert>
        )}
        <Select
          id="kh-supply-method"
          label={t('dosing.khSupply.form.method')}
          value={method}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            setMethod(e.target.value as KhSupplyMethod);
            setErrors({});
          }}
          options={KH_SUPPLY_METHODS.map((m) => ({ value: m, label: t(`dosing.khSupply.methods.${m}`) }))}
        />
        <Text variant="muted" size="sm">{t(`dosing.khSupply.methodHelp.${method}`)}</Text>
        {fields.map((field) => (
          <Input
            key={`${method}-${field}`}
            id={`kh-supply-${field}`}
            type="text"
            inputMode="decimal"
            label={t(field === 'solutionMl' ? `dosing.khSupply.form.solutionMl.label_${method}` : `dosing.khSupply.form.${field}.label`)}
            helperText={t(`dosing.khSupply.form.${field}.help`)}
            value={values[field]}
            onChange={setValue(field)}
            error={errors[field]}
            required={required.has(field)}
          />
        ))}
        <Inline gap="3" justify="end">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('dosing.form.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {t('dosing.khSupply.form.submit')}
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}

export default KhSupplyForm;
